import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { trackShare, trackButtonClick } from '../../../utils/googleAnalyticsService';

const ShareModal = ({ isOpen, onClose, tripId, tripTitle }) => {
  const [copied, setCopied] = useState(false);
  const tripUrl = `${window.location.origin}/trip/${tripId}`;

  const handleShare = (platform) => {
    // Track share action
    trackShare(platform, 'trip', tripId);
    trackButtonClick(`share_${platform}`, `Share on ${platform}`, {
      trip_id: tripId,
      trip_title: tripTitle,
    });

    const shareUrl = {
      'facebook': `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(tripUrl)}`,
      'twitter': `https://twitter.com/intent/tweet?url=${encodeURIComponent(tripUrl)}&text=${encodeURIComponent(`Check out my ${tripTitle} trip plan!`)}`
    }[platform];

    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
  };

  const handleCopyLink = async () => {
    // Track copy link action
    trackButtonClick('copy_trip_link', 'Copy Link', {
      trip_id: tripId,
    });

    try {
      await navigator.clipboard?.writeText(tripUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const shareOptions = [
    {
      name: "Facebook",
      icon: "Facebook",
      color: "bg-blue-600",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(tripUrl)}`
    },
    {
      name: "Twitter",
      icon: "Twitter",
      color: "bg-sky-500",
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(tripUrl)}&text=${encodeURIComponent(`Check out my ${tripTitle} trip plan!`)}`
    },
    {
      name: "WhatsApp",
      icon: "MessageCircle",
      color: "bg-green-600",
      url: `https://wa.me/?text=${encodeURIComponent(`Check out my ${tripTitle} trip plan: ${tripUrl}`)}`
    },
    {
      name: "Email",
      icon: "Mail",
      color: "bg-gray-600",
      url: `mailto:?subject=${encodeURIComponent(`${tripTitle} - Trip Plan`)}&body=${encodeURIComponent(`I wanted to share my trip plan with you: ${tripUrl}`)}`
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-organic shadow-organic-lg w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="font-heading font-heading-bold text-lg text-foreground">
            Share Trip
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-organic-sm transition-organic"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="font-body font-body-medium text-sm text-foreground mb-2 block">
              Trip URL
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                value={tripUrl}
                readOnly
                className="flex-1"
              />
              <Button
                variant={copied ? "success" : "outline"}
                size="sm"
                iconName={copied ? "Check" : "Copy"}
                onClick={handleCopyLink}
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>

          <div>
            <h4 className="font-body font-body-medium text-sm text-foreground mb-3">
              Share on social media
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {shareOptions?.map((option) => (
                <a
                  key={option?.name}
                  href={option?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 p-3 ${option?.color} text-white rounded-organic hover:opacity-90 transition-organic`}
                >
                  <Icon name={option?.icon} size={20} />
                  <span className="font-body text-sm">{option?.name}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="bg-muted rounded-organic-sm p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
              <div>
                <p className="font-body text-sm text-foreground">
                  Anyone with this link can view your trip plan
                </p>
                <p className="font-caption text-xs text-muted-foreground mt-1">
                  They won't be able to edit or modify your trip
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;